import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, ChevronDown, ChevronUp } from 'lucide-react';
import { companyInfo, services, navLinks } from '../../data/mock';
import { getAllCouncils } from '../../data/councilData';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showAllCouncils, setShowAllCouncils] = useState(false);
  const councils = getAllCouncils();

  const socialLinks = {
    facebook: "https://www.facebook.com/profile.php?id=61577688976534",
    instagram: "https://www.instagram.com/supaenergyhub"
  };

  // Group councils by region for better organization
  const councilsByRegion = {
    "Western Melbourne": councils.filter(c => ["brimbank", "hobsons-bay", "maribyrnong", "melton", "moonee-valley", "wyndham"].includes(c.slug)),
    "Northern Melbourne": councils.filter(c => ["darebin", "hume", "merri-bek", "whittlesea", "banyule", "nillumbik"].includes(c.slug)),
    "Eastern Melbourne": councils.filter(c => ["boroondara", "knox", "manningham", "maroondah", "whitehorse", "yarra-ranges"].includes(c.slug)),
    "South-Eastern Melbourne": councils.filter(c => ["bayside", "cardinia", "casey", "frankston", "glen-eira", "greater-dandenong", "kingston", "monash", "mornington-peninsula"].includes(c.slug)),
    "Inner Melbourne": councils.filter(c => ["melbourne", "port-phillip", "stonnington", "yarra"].includes(c.slug)),
  };

  // Featured councils (shown by default)
  const featuredCouncils = councils.slice(0, 8);

  return (
    <footer className="bg-[#3D5A47] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-5">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.brandName} 
                className="h-12 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-[#B5D43B] font-medium text-sm mb-3">
              {companyInfo.motto}
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              {companyInfo.tagline}
            </p>
            <div className="space-y-3 mb-6">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-3 text-gray-300 hover:text-[#B5D43B] transition-colors text-sm"
              >
                <Phone className="w-4 h-4 text-[#B5D43B]" />
                {companyInfo.phone}
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-3 text-gray-300 hover:text-[#B5D43B] transition-colors text-sm"
              >
                <Mail className="w-4 h-4 text-[#B5D43B]" />
                {companyInfo.email}
              </a>
              <div className="flex items-center gap-3 text-gray-300 text-sm">
                <MapPin className="w-4 h-4 text-[#B5D43B]" />
                {companyInfo.location}
              </div>
            </div>
            {/* Social Media Links */}
            <div className="flex items-center gap-3">
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#B5D43B] flex items-center justify-center transition-all group"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="w-5 h-5 text-white group-hover:text-[#3D5A47]" />
              </a>
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#B5D43B] flex items-center justify-center transition-all group"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5 text-white group-hover:text-[#3D5A47]" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-semibold text-base mb-5">Our Services</h4>
            <ul className="space-y-3">
              {services.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services#${service.id}`}
                    className="text-gray-300 hover:text-[#B5D43B] transition-colors text-sm"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="font-semibold text-base mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.flatMap((link) =>
                link.children
                  ? link.children.map((child) => ({ ...child, _key: child.href }))
                  : [{ ...link, _key: link.href }]
              ).map((link) => (
                <li key={link._key}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-[#B5D43B] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/how-it-works"
                  className="text-gray-300 hover:text-[#B5D43B] transition-colors text-sm"
                >
                  How It Works
                </Link>
              </li>
              <li>
                <Link
                  to="/trust"
                  className="text-gray-300 hover:text-[#B5D43B] transition-colors text-sm"
                >
                  Trust & Compliance
                </Link>
              </li>
            </ul>
          </div>

          {/* Accreditations Column */}
          <div>
            <h4 className="font-semibold text-base mb-5">Accreditations</h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-lg">
                <img 
                  src="/netcc-approved-seller.png" 
                  alt="NETCC Approved Seller" 
                  className="h-5 w-auto brightness-0 invert"
                />
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-lg">
                <svg className="w-5 h-5 text-[#B5D43B]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                </svg>
                <span className="text-sm text-gray-300">Solar Victoria Approved</span>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-lg">
                <svg className="w-5 h-5 text-[#B5D43B]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                </svg>
                <span className="text-sm text-gray-300">Accredited Installers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Service Areas Section */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h4 className="font-semibold text-base mb-6 text-center">Service Areas - Solar & Battery Installation</h4>
          
          {!showAllCouncils ? (
            // Show featured councils in a simple grid
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mb-4">
              {featuredCouncils.map((council) => (
                <Link
                  key={council.slug}
                  to={`/solar/${council.slug}`}
                  className="text-gray-400 hover:text-[#B5D43B] text-xs transition-colors text-center py-1"
                >
                  {council.name}
                </Link>
              ))}
            </div>
          ) : (
            // Show all councils organized by region
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-4">
              {Object.entries(councilsByRegion).map(([region, regionCouncils]) => (
                <div key={region}>
                  <h5 className="text-[#B5D43B] text-xs font-semibold mb-2">{region}</h5>
                  <ul className="space-y-1">
                    {regionCouncils.map((council) => (
                      <li key={council.slug}>
                        <Link
                          to={`/solar/${council.slug}`}
                          className="text-gray-400 hover:text-[#B5D43B] text-xs transition-colors"
                        >
                          {council.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          
          <button
            onClick={() => setShowAllCouncils(!showAllCouncils)}
            className="flex items-center gap-1 text-[#B5D43B] text-xs mx-auto hover:underline"
          >
            {showAllCouncils ? (
              <>Show Less <ChevronUp className="w-3 h-3" /></>
            ) : (
              <>View All 31 Melbourne Councils <ChevronDown className="w-3 h-3" /></>
            )}
          </button>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} {companyInfo.legalEntity} trading as {companyInfo.brandName}. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-gray-200 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className="text-gray-400 hover:text-gray-200 text-sm transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/customer-service-policy" className="text-gray-400 hover:text-gray-200 text-sm transition-colors">
                Customer Service Policy
              </Link>
              <Link to="/customer-care-support-policy" className="text-gray-400 hover:text-gray-200 text-sm transition-colors">
                Customer Care Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
